import { getListFlowsQueryKey, useCreateFlow } from '@reelplicate/api-client/flows';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

/** The template "Make my version" starts, by asset kind (either case). */
const TEMPLATES: Record<string, 'replicate-reel' | 'replicate-carousel'> = {
  reel: 'replicate-reel',
  carousel: 'replicate-carousel',
  image: 'replicate-carousel',
};

const templateFor = (kind: string) => TEMPLATES[kind.toLowerCase()];

/** Reels, carousels and photos can be remade; other kinds can't yet. */
export const canMakeMyVersion = (kind: string) => templateFor(kind) !== undefined;

/**
 * "Make my version": a flow seeded with a library asset - Replicate a reel,
 * or Remake a carousel for carousels and photos - opened in the editor.
 * Failures are shown as a toast by the API client.
 */
export function useMakeMyVersion() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const create = useCreateFlow();

  const start = async (asset: { id: string; kind: string }) => {
    const templateKey = templateFor(asset.kind);
    if (!templateKey) return;
    try {
      const flow = await create.mutateAsync({
        id: workspaceId,
        data: { templateKey, sourceAssetId: asset.id },
      });
      await queryClient.invalidateQueries({
        queryKey: getListFlowsQueryKey(workspaceId),
      });
      void navigate(`/flows/${flow.id}`);
    } catch {
      return;
    }
  };

  return {
    start,
    /** The asset a flow is being made from, while it's being made. */
    pendingAssetId: create.isPending ? create.variables?.data.sourceAssetId : undefined,
    ready: Boolean(workspaceId),
  };
}
